import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaBook, FaCopy, FaCheck, FaPrint, FaSyncAlt, FaUserTie, FaQuestionCircle } from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { Link } from 'react-router-dom';

// ============================================
// ACCOUNTING & QUICKBOOKS GUIDE
// A single source of truth for how the accounting side works, for the
// accountant. The same content powers the "Copy for email" button so a
// plain-text version can be pasted into an email without re-typing.
// ============================================

const GUIDE = [
  {
    title: '1. The big picture',
    intro: 'Our transport system (the TMS) is where the work happens — quotes, jobs, loads, invoices, subbie costs and payments. QuickBooks is the books of record.',
    bullets: [
      'The flow is ONE-WAY: we push from the TMS → into QuickBooks. Nothing is ever pulled back from QuickBooks into the TMS.',
      'Nothing is pushed automatically. A person presses the button on Accounts Overview. That is deliberate: nothing lands in the books until finance has reviewed it.',
      'Every push is safe to repeat. If you press a button twice, you get an update — never a duplicate.',
      'Every push attempt is written to the Sync Log (success / skipped / failed) with the reason, so an accountant can always see what happened and why.',
    ],
  },
  {
    title: '2. What we push, and what it becomes in QuickBooks',
    intro: 'Each row below is one "money object" in our system and its QuickBooks equivalent.',
    table: {
      head: ['In our system (TMS)', 'Becomes in QuickBooks', 'Notes'],
      rows: [
        ['Client', 'Customer', 'Created once, then reused for every invoice.'],
        ['Subcontractor', 'Vendor (Supplier)', 'Needed before their bills or payments can be pushed.'],
        ['Tax invoice (INV-…)', 'Invoice', 'Same document number. Proformas (PF-…) are never pushed.'],
        ['Client payment received', 'Payment linked to that invoice', 'Partial payments are supported and each one is dated.'],
        ['Subbie cost / payable (OPL-…)', 'Bill', 'What we owe the subcontractor.'],
        ['Each payment we make to a subbie', 'Bill Payment against that bill', 'A deposit and a balance become TWO bill payments.'],
        ['Money a subbie pays back (recovery)', 'Vendor Credit (supplier credit)', 'Not a negative payment — it is a supplier credit you can apply.'],
        ['Credit note to a client (CN-…)', 'Credit Memo applied to the invoice', 'Applied, so the client is not shown owing AND holding a credit.'],
        ['Cancelled subbie payable', 'The Bill is voided', 'A bill that already has a payment must have that payment reversed first.'],
      ],
    },
  },
  {
    title: '3. What NEVER goes to QuickBooks',
    bullets: [
      'Proformas and quotations (PF-…), drafts and anything not yet a real tax invoice.',
      'Operational detail: jobs, load line items, allocations, PODs, permits, driver/live-map data, rates and margins.',
      'Nothing is ever deleted from QuickBooks by us. Cancellations are handled with voids and credits, so the audit trail stays intact.',
    ],
  },
  {
    title: '4. Numbers, VAT and matching',
    bullets: [
      'Our document numbers are used as the QuickBooks document numbers, so anything can be matched by number (e.g. INV-00009 in both).',
      'QuickBooks still assigns its own internal Id — we store both, which is exactly what makes re-syncing an update instead of a duplicate.',
      'VAT: 15% is charged by law and is the default. Zero-rated is used only for exports (cross-border loads) or where the invoice was issued with no VAT. The correct QuickBooks tax code is picked automatically.',
      'If a document is edited in the TMS after it was pushed, the next sync updates it in QuickBooks (never a second copy).',
    ],
  },
];

const MANUAL = [
  'Bank reconciliation — match the payments and bill payments we push against the actual bank lines.',
  'Supplier credits (money back) — review and apply the Vendor Credits against the next bill, or refund them out of the bank.',
  'Anything posted directly in QuickBooks (journals, payroll, statutory returns, VAT201, depreciation). We never touch QBO-native transactions.',
  'Housekeeping in QuickBooks: chart of accounts, VAT codes/rates, and any customer/vendor merge or clean-up.',
];
const STEPS = [
  { step: '1', title: 'Connect QuickBooks', body: 'Accounts Overview → Connect QuickBooks. An admin signs into Intuit once. The connection then stays alive by itself (tokens refresh automatically).' },
  { step: '2', title: 'Push Customers & Suppliers first', body: 'Nothing else can work until the client and the subbie exist in QuickBooks. Always do this before invoices or bills.' },
  { step: '3', title: 'Push Invoices & Bills', body: 'Sends tax invoices, client payments, subbie payables (bills) and subbie payments. The order inside the button matters: documents are created before the payments that settle them.' },
  { step: '4', title: 'Push Credit Notes & Void Bills', body: 'Credit notes become credit memos applied to the original invoice; cancelled subbie payables have their QuickBooks bill voided; money returned by a subbie becomes a vendor credit.' },
  { step: '5', title: 'Check the Sync Log', body: 'Click Sync Log. Anything "skipped" or "failed" says exactly why (see the list below) — fix the cause and press the button again. Repeating is always safe.' },
];

const TROUBLE = [
  ['customer / supplier not in QBO yet', 'Press "Sync Customers & Suppliers" first.'],
  ['invoice not yet pushed to QBO', 'Push "Invoices & Bills" first, then run the payments sync again.'],
  ['no QBO bank account found', 'Make sure an active Bank account exists in QuickBooks (or name it in the backend .env as QBO_PAYMENT_BANK_ACCOUNT).'],
  ['no QBO Service item available', 'Create one Service item in QuickBooks (we create one automatically if an Income account exists).'],
  ['bill has a payment, cannot be voided', 'Reverse that bill payment in QuickBooks first, then re-run "Credit Notes & Void Bills".'],
  ['Required param missing (Intuit)', 'QuickBooks rejected a field — the Sync Log line carries the Intuit trace id; send it to us and we fix the mapping.'],
];

const WHY_MANUAL = [
  'You stay in control of what reaches the books, and when — important at month-end and during audits.',
  'A staged push means a document can still be corrected here before it ever reaches QuickBooks.',
  'Because every push is idempotent, "press it again" is always the safe answer to a partial failure.',
];

function AccountingHelpPage({ onLogout }) {
  const [copied, setCopied] = useState(false);

  const buildPlainText = () => {
    const lines = [];
    lines.push('HOW OUR ACCOUNTING & QUICKBOOKS INTEGRATION WORKS');
    lines.push('='.repeat(56));
    lines.push('');
    GUIDE.forEach((s) => {
      lines.push(s.title.toUpperCase());
      if (s.intro) lines.push(s.intro);
      (s.bullets || []).forEach((b) => lines.push('  - ' + b));
      if (s.table) {
        lines.push('  ' + s.table.head.join(' | '));
        s.table.rows.forEach((r) => lines.push('  ' + r.join(' | ')));
      }
      lines.push('');
    });
    lines.push('HOW TO PUSH (Accounts Overview → QuickBooks card)');
    STEPS.forEach((s) => lines.push('  ' + s.step + '. ' + s.title + ' — ' + s.body));
    lines.push('');
    lines.push('WHAT THE ACCOUNTANT STILL DOES MANUALLY IN QUICKBOOKS');
    MANUAL.forEach((m) => lines.push('  - ' + m));
    lines.push('');
    lines.push('IF SOMETHING WAS SKIPPED (check the Sync Log)');
    TROUBLE.forEach((t) => lines.push('  - ' + t[0] + ' → ' + t[1]));
    lines.push('');
    lines.push('WHY THE PUSH IS MANUAL, NOT AUTOMATIC');
    WHY_MANUAL.forEach((w) => lines.push('  - ' + w));
    return lines.join('\n');
  };

  const copyGuide = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      setCopied(true);
      toast.success('Guide copied — paste it into an email');
      setTimeout(() => setCopied(false), 4000);
    } catch {
      toast.error('Could not copy automatically — use Print / PDF instead');
    }
  };

  return (
    <AdminLayout activeTab="help" onLogout={onLogout}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
              <FaBook className="text-green" /> Accounting &amp; QuickBooks Guide
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              How the money side works, what syncs to QuickBooks, what does not, and what stays manual — written so it can be handed straight to an accountant.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={copyGuide}
              className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy transition-colors flex items-center gap-2"
            >
              {copied ? <FaCheck size={14} /> : <FaCopy size={14} />}
              {copied ? 'Copied!' : 'Copy for email'}
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <FaPrint size={14} /> Print / PDF
            </button>
          </div>
        </div>


        {/* The guide */}
        {GUIDE.map((section) => (
          <div key={section.title} className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-bold text-navy mb-2">{section.title}</h2>
            {section.intro && <p className="text-sm text-gray-600 mb-3">{section.intro}</p>}
            {section.table && (
              <div className="overflow-x-auto mb-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      {section.table.head.map((h) => <th key={h} className="pb-2 pr-4">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows.map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#E8ECEF' }}>
                        {r.map((cell, j) => (
                          <td key={j} className={`py-2 pr-4 ${j === 0 ? 'font-medium text-navy' : 'text-gray-600'}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {section.bullets && (
              <ul className="space-y-2">
                {section.bullets.map((b, i) => (
                  <li key={i} className="text-sm text-gray-600 flex gap-2">
                    <span className="text-green mt-0.5">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {/* How to push */}
        <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
            <FaSyncAlt className="text-green" /> 5. How to push — step by step
          </h2>
          <div className="space-y-3">
            {STEPS.map((s) => (
              <div key={s.step} className="flex gap-3">
                <div className="w-7 h-7 shrink-0 rounded-full bg-green text-white text-sm font-bold flex items-center justify-center">{s.step}</div>
                <div>
                  <p className="font-medium text-navy text-sm">{s.title}</p>
                  <p className="text-sm text-gray-600">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual work for the accountant */}
        <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
            <FaUserTie className="text-green" /> 6. What the accountant still does in QuickBooks
          </h2>
          <ul className="space-y-2">
            {MANUAL.map((m, i) => (
              <li key={i} className="text-sm text-gray-600 flex gap-2">
                <span className="text-green mt-0.5">•</span><span>{m}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="text-lg font-bold text-navy mb-3 flex items-center gap-2">
            <FaQuestionCircle className="text-green" /> 7. If something was skipped (read the Sync Log)
          </h2>
          <div className="space-y-2">
            {TROUBLE.map(([q, a], i) => (
              <div key={i} className="text-sm">
                <p className="font-medium text-navy">{q}</p>
                <p className="text-gray-600">→ {a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why manual */}
        <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="text-lg font-bold text-navy mb-3">8. Why the push is manual, not automatic</h2>
          <ul className="space-y-2">
            {WHY_MANUAL.map((w, i) => (
              <li key={i} className="text-sm text-gray-600 flex gap-2">
                <span className="text-green mt-0.5">•</span><span>{w}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-sm text-gray-500 flex flex-wrap items-center gap-4">
          <Link to="/help" className="text-gray-500 font-medium hover:underline">← Back to Help Centre</Link>
          <Link to="/accounts-overview" className="text-green font-medium hover:underline">Go to Accounts Overview → QuickBooks</Link>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AccountingHelpPage;

